/**
 * The drill-down contract (pivot doc §3): every metric tile, table row and
 * insight carries an entity reference and a filters payload, and following it
 * pushes both into the URL. Overview → competition → sources → campaign →
 * creative is then a chain of ordinary links, each one shareable.
 *
 * Entity types double as filter dimensions where one exists (`competition`,
 * `campaign`, `channel`, `creative`), so arriving at a page "for" an entity and
 * filtering that page by it are the same URL.
 */

import { serializeFilters, type GlobalFilters } from '@/lib/filters'

export const ENTITY_TYPES = [
  'competition',
  'campaign',
  'channel',
  'creative',
  'segment',
  'customer',
] as const
export type EntityType = (typeof ENTITY_TYPES)[number]

export interface EntityRef {
  type: EntityType
  id: string
  label: string
}

export interface DrillDown {
  /** A route from the route table, with any `:param` already filled in. */
  path: string
  entity?: EntityRef
  /** Page-specific context, e.g. `{ stage: 'checkout_started' }`. */
  params?: Record<string, string>
}

export function drillDownHref(target: DrillDown, filters: GlobalFilters): string {
  const query = serializeFilters(filters)
  if (target.entity) query.set(target.entity.type, target.entity.id)
  for (const [key, value] of Object.entries(target.params ?? {})) query.set(key, value)
  const search = query.toString()
  return search ? `${target.path}?${search}` : target.path
}
